import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArchiveRoundedIcon from "@mui/icons-material/ArchiveRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import {
  archiveDiagramVersion,
  createContentObject,
  downloadDiagramFile,
  getContentObject,
  getViewConfig,
  listContentObjectTypes,
  listContentObjects,
  listDiagramVersions,
  listUsedAttributes,
  listUnlinkedContent,
  refreshContentCatalogue,
  restoreContentLink,
  restoreDiagramVersion,
  unlinkContentObject,
  updateContentObject,
} from "../../../actions";
import { canEditDatabase, contentDatabases, startingDatabase } from "../../../utils/permissions";
import colourForType from "../../../utils/typeColours";
import { attributeLabel, EMPTY_VIEW_CONFIG, setting, typeLabel } from "../../../utils/viewConfig";
import { brand } from "../../../theme/businessFlowsTheme";
import { Caution, EmptyState, Hint, Refusal, Surface, Toast, focusRing, quietDanger } from "../../workspaces/WorkspaceUi";
import CreateObjectDialog from "./CreateObjectDialog";
import FileUpload from "./FileUpload";
import { formatSize } from "./fileFormats";
import { mapPath } from "../../../utils/deepLinks";

// The texts offered for every object ("Texts in the content editor" under
// Views -> Settings), and the attributes never edited here (the protected
// ones and the database's hidden attributes).
const textFieldsFor = (config) =>
  (setting(config, "editor.fields") || []).map((key) => ({
    key,
    label: attributeLabel(key),
    rows: key === "name" ? 1 : key === "Description" ? 4 : 2,
  }));

const hiddenFieldsFor = (config) => [...(config?.protectedFields || []), ...(setting(config, "ignoredAttributes") || [])];

// The standard texts, plus any other attribute this object already carries, so
// content added by an import or by another admin can be edited too.
const fieldsForObject = (object, extras = [], config = null) => {
  const text = textFieldsFor(config);
  const hidden = hiddenFieldsFor(config);
  const own = Object.keys(object || {})
    .filter((key) => key.charAt(0) !== "_" && !hidden.includes(key) && !text.some((f) => f.key === key))
    .sort((a, b) => a.localeCompare(b))
    .map((key) => ({ key, label: attributeLabel(key), rows: 2 }));
  const added = extras
    .filter((key) => !text.some((f) => f.key === key) && !own.some((f) => f.key === key))
    .map((key) => ({ key, label: attributeLabel(key), rows: 2 }));
  return [...text, ...own, ...added];
};

const fieldsFromObject = (object, fieldList) =>
  Object.fromEntries(fieldList.map(({ key }) => [key, object?.[key] ?? ""]));

// How a link to a parent reads. Most content points at its parent; an object
// created with the direction switched is pointed at by its parent instead, and
// the line says so - otherwise the same relationship name would describe two
// different pictures.
export const linkWording = (link) =>
  link?.direction === "fromParent" ? `linked as ${link.relationship}, from the parent` : `linked as ${link?.relationship}`;

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const menuProps = { PaperProps: { className: "bfx", sx: { borderRadius: 3, border: `1px solid ${brand.line}`, maxHeight: 360 } } };

export default function ContentManager({ user }) {
  // Staff: every database; customers: the ones they edit.
  const databases = useMemo(() => contentDatabases(user), [user]);
  const labelFor = useCallback((name) => user.databaseLabels?.[name] || name, [user.databaseLabels]);

  // Start where the person is working, then fall back.
  const [database, setDatabase] = useState(() => startingDatabase(user, databases));
  const mayManage = canEditDatabase(user, database);
  const [types, setTypes] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [query, setQuery] = useState("");
  const [objects, setObjects] = useState(null);
  const [selectedGuid, setSelectedGuid] = useState(null);
  const [context, setContext] = useState(null);
  const [versions, setVersions] = useState([]);
  const [unlinked, setUnlinked] = useState([]);
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  // false, or the object to create under (null: the editor picks one).
  const [createOpen, setCreateOpen] = useState(false);
  const [createParent, setCreateParent] = useState(null);
  const [attributeOpen, setAttributeOpen] = useState(false);
  // Attributes an admin added to this object in this session, before saving.
  const [addedFields, setAddedFields] = useState([]);
  const listId = useRef(0);
  // Bumped by Refresh: the type filter and the object list are read again.
  const [refreshTick, setRefreshTick] = useState(0);

  // How the chosen database is configured (labels, colours, editor texts).
  // Before it arrives (or if the database has no configuration) the fallback
  // still protects Type and the bookkeeping fields, so editable fields never
  // include them.
  const [viewConfig, setViewConfig] = useState(EMPTY_VIEW_CONFIG);
  const viewConfigRef = useRef(EMPTY_VIEW_CONFIG);
  viewConfigRef.current = viewConfig;
  useEffect(() => {
    if (!database) return;
    let active = true;
    getViewConfig(database).then((res) => {
      if (active) setViewConfig(res?.success ? res.content : EMPTY_VIEW_CONFIG);
    });
    return () => {
      active = false;
    };
  }, [database]);

  useEffect(() => {
    if (!database) return;
    setError("");
    setTypeFilter("");
    setQuery("");
    setSelectedGuid(null);
    setContext(null);
    setVersions([]);
    setUnlinked([]);
  }, [database]);

  // The types on offer, re-read when an editor asks for a refresh.
  useEffect(() => {
    if (!database) return;
    listContentObjectTypes(database).then((res) => setTypes(res?.success ? res.content : []));
  }, [database, refreshTick]);

  // Prepopulated list; search and type filter narrow it.
  useEffect(() => {
    if (!database) return undefined;
    const id = ++listId.current;
    setObjects(null);
    const timer = setTimeout(async () => {
      const res = await listContentObjects(database, { query: query.trim(), type: typeFilter, limit: 80 });
      if (id !== listId.current) return;
      setObjects(res?.success ? res.content : []);
      if (!res?.success) setError(res?.msg || "Couldn't list objects.");
    }, query ? 350 : 0);
    return () => clearTimeout(timer);
  }, [database, query, typeFilter, refreshTick]);

  const loadSelection = useCallback(
    async (guid) => {
      if (!guid || !database) return;
      const [detail, versionList, removed] = await Promise.all([
        getContentObject(database, guid),
        listDiagramVersions(database, guid),
        listUnlinkedContent(database, guid),
      ]);
      if (detail?.success) {
        setContext(detail.content);
        const object = detail.content.object || {};
        setAddedFields([]);
        setFields(fieldsFromObject(object, fieldsForObject(object, [], viewConfigRef.current)));
      } else setError(detail?.msg || "Couldn't load the object.");
      setVersions(versionList?.success ? versionList.content : []);
      setUnlinked(removed?.success ? removed.content : []);
    },
    [database]
  );

  useEffect(() => {
    if (selectedGuid) loadSelection(selectedGuid);
  }, [selectedGuid, loadSelection]);

  // Types and relationships are discovered once every five minutes, so one an
  // import wrote a moment ago is missing from these lists until then. This
  // reads the database again now; asked twice within ten seconds it says so
  // rather than scanning again, which is a note, not a failure.
  const refreshCatalogue = async () => {
    setBusy("refresh");
    setError("");
    const res = await refreshContentCatalogue(database);
    setBusy("");
    if (res?.success === false && res?.reason !== "too-many-attempts") {
      setError(res?.msg || "Couldn't read this database's types again.");
      return;
    }
    if (res?.success) {
      setRefreshTick((tick) => tick + 1);
      getViewConfig(database).then((config) => setViewConfig(config?.success ? config.content : EMPTY_VIEW_CONFIG));
    }
    setToast(res?.msg || "Up to date.");
  };

  const run = async (key, fn, message) => {
    setBusy(key);
    setError("");
    const res = await fn();
    setBusy("");
    if (res?.success === false) {
      setError(res.msg || "That didn't work.");
      return false;
    }
    setToast(message);
    await loadSelection(selectedGuid);
    return true;
  };

  // A file to save: fetched with the sign-in header, handed to the browser as a download.
  const download = async (version) => {
    setBusy(`download-${version.id}`);
    const res = await downloadDiagramFile(database, selectedGuid, version.format);
    setBusy("");
    if (!res?.success) {
      setError(res?.msg || "Couldn't download the file.");
      return;
    }
    const link = document.createElement("a");
    link.href = URL.createObjectURL(res.blob);
    link.download = res.fileName || version.fileName || `${version.name || "file"}.${version.format}`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  };

  const openCreate = (parentObject) => {
    setCreateParent(parentObject || null);
    setCreateOpen(true);
  };

  const object = context?.object;
  // The same child can be linked more than once; show each once, grouped by type.
  const uniqueChildren = useMemo(() => {
    const seen = new Map();
    (context?.children || []).forEach((child) => {
      if (child.guid && !seen.has(child.guid)) seen.set(child.guid, child);
    });
    return [...seen.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [context]);
  const childGroups = useMemo(() => {
    const groups = new Map();
    uniqueChildren.forEach((child) => {
      const type = child.type || "Other";
      groups.set(type, [...(groups.get(type) || []), child]);
    });
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [uniqueChildren]);
  const editableFields = useMemo(() => fieldsForObject(object, addedFields, viewConfig), [object, addedFields, viewConfig]);
  const dirty = object ? editableFields.some(({ key }) => (fields[key] || "") !== (object[key] || "")) : false;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, ...focusRing }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: brand.ink, color: brand.signal }}>
          <AccountTreeRoundedIcon />
        </Box>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Objects and diagrams
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Browse the library, edit an object's texts, attach diagrams and files, and add objects of any type under any object.
          </Typography>
        </Box>
        {mayManage && (
          <Button variant="outlined" size="small" startIcon={<AddRoundedIcon />} onClick={() => openCreate(null)} sx={{ ml: "auto !important", flexShrink: 0 }}>
            New object
          </Button>
        )}
      </Stack>

      {!mayManage && (
        <Hint sx={{ mb: 2 }} next="Editing needs Edit rights: ask your workspace admin.">
          You can browse this database.
        </Hint>
      )}
      {error && (
        <Refusal sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Refusal>
      )}

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "360px minmax(0, 1fr)" }, alignItems: "start" }}>
        {/* Browser */}
        <Surface>
          <Stack spacing={1.5} sx={{ p: 2 }}>
            <TextField
              select
              size="small"
              label="Database"
              value={database}
              onChange={(e) => setDatabase(e.target.value)}
              SelectProps={{ MenuProps: menuProps }}
            >
              {databases.map((name) => (
                <MenuItem key={name} value={name}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <StorageRoundedIcon fontSize="small" sx={{ color: brand.signalDeep }} />
                    <span>{labelFor(name)}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} SelectProps={{ MenuProps: menuProps }}>
              <MenuItem value="">All types</MenuItem>
              {types.map((type) => (
                <MenuItem key={type} value={type}>
                  {typeLabel(viewConfig, type)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              placeholder="Search by name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Read this database's object types and relationships again. New ones can take a few minutes to appear on their own.">
              <span>
                <IconButton size="small" disabled={!database || busy === "refresh"} onClick={refreshCatalogue} aria-label="Refresh the types and relationships">
                  {busy === "refresh" ? <CircularProgress size={18} /> : <RefreshRoundedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Divider />
          <Box sx={{ maxHeight: 520, overflowY: "auto" }}>
            {objects === null ? (
              <Box sx={{ p: 2 }}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} height={38} />
                ))}
              </Box>
            ) : objects.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Nothing found in {labelFor(database)}.
                </Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />}>
                {objects.map((item) => (
                  <Box
                    key={item.guid}
                    onClick={() => setSelectedGuid(item.guid)}
                    sx={{
                      px: 2,
                      py: 1.25,
                      cursor: "pointer",
                      borderLeft: `4px solid ${item.guid === selectedGuid ? brand.signal : "transparent"}`,
                      bgcolor: item.guid === selectedGuid ? brand.canvas : "transparent",
                      "&:hover": { bgcolor: brand.canvas },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: colourForType(item.type, viewConfig), flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {typeLabel(viewConfig, item.type)}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.25}>
                        {(item.formats || []).map((format) => (
                          <Chip key={format} size="small" label={format.toUpperCase()} sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700 }} />
                        ))}
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Surface>

        {/* Selected object */}
        {!selectedGuid ? (
          <Surface sx={{ borderStyle: "dashed" }}>
            <EmptyState icon={<AccountTreeRoundedIcon />} title="Pick an object">
              Choose one from the list to see where it sits, edit its texts and manage its diagrams.
            </EmptyState>
          </Surface>
        ) : !context ? (
          <Skeleton variant="rounded" height={420} sx={{ borderRadius: 4 }} />
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 1fr) minmax(0, 1fr)" },
              alignItems: "start",
            }}
          >
            {/* Header and hierarchy */}
            <Surface sx={{ p: 2.5, gridColumn: { xl: "1 / -1" } }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
                <Stack direction="row" spacing={1.25} sx={{ minWidth: 0 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colourForType(object.Type, viewConfig), mt: 0.9, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {typeLabel(viewConfig, object.Type)}
                      {object.inTrial ? " · in trial" : ""}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.15rem", overflowWrap: "anywhere", lineHeight: 1.25 }}>
                      {object.name}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {labelFor(database)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.disabled", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                        {object.GUID}
                      </Typography>
                      <Tooltip title="Copy id">
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard?.writeText(object.GUID);
                            setToast("Id copied.");
                          }}
                          aria-label="Copy object id"
                        >
                          <ContentCopyRoundedIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button
                    size="small"
                    startIcon={<OpenInNewRoundedIcon />}
                    href={mapPath(database, object.GUID)}
                    target="_blank"
                    rel="noopener"
                  >
                    Viewer
                  </Button>
                  {mayManage && (
                    <Button size="small" variant="contained" startIcon={<AddRoundedIcon />} onClick={() => openCreate(object)}>
                      Add child
                    </Button>
                  )}
                </Stack>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: "block", mb: 0.75 }}>
                Where it sits
              </Typography>
              {context.parents.length === 0 ? (
                <Caution sx={{ mb: 1 }} next="Restore a link below, or add it under a parent.">
                  This object has no parent, so it doesn't appear in the hierarchy.
                </Caution>
              ) : (
                <Stack spacing={0.75}>
                  {context.parents.map((parent) => (
                    <Stack
                      key={`${parent.guid}-${parent.relationship}`}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ p: 1, borderRadius: 2.5, bgcolor: brand.canvas }}
                    >
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedGuid(parent.guid)}
                        onKeyDown={(e) => e.key === "Enter" && setSelectedGuid(parent.guid)}
                        sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0, cursor: "pointer" }}
                      >
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: colourForType(parent.type, viewConfig), flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                            {parent.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {typeLabel(viewConfig, parent.type)} · {linkWording(parent)}
                          </Typography>
                        </Box>
                        <ChevronRightRoundedIcon sx={{ fontSize: 16, color: "text.disabled", ml: "auto" }} />
                      </Box>
                      {mayManage && (
                        <Tooltip title="Unlink from this parent. The object stays and the link can be restored.">
                          <IconButton
                            size="small"
                            disabled={busy === `unlink-${parent.guid}`}
                            onClick={() =>
                              run(
                                `unlink-${parent.guid}`,
                                () => unlinkContentObject(database, object.GUID, parent.guid),
                                "Unlinked. The object stays and the link can be restored."
                              )
                            }
                            aria-label={`Unlink from ${parent.name}`}
                            sx={quietDanger}
                          >
                            <LinkOffRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}

              {childGroups.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: "block", mt: 2, mb: 0.75 }}>
                    Contains {uniqueChildren.length} {uniqueChildren.length === 1 ? "object" : "objects"}
                  </Typography>
                  <Box sx={{ maxHeight: 260, overflowY: "auto", pr: 0.5 }}>
                    {childGroups.map(([type, group]) => (
                      <Box key={type} sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: colourForType(type, viewConfig) }} />
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            {typeLabel(viewConfig, type)}
                          </Typography>
                          <Chip size="small" label={group.length} sx={{ height: 16, fontSize: "0.62rem" }} />
                        </Stack>
                        <Stack>
                          {group.map((child) => (
                            <Box
                              key={child.guid}
                              role="button"
                              tabIndex={0}
                              onClick={() => setSelectedGuid(child.guid)}
                              onKeyDown={(e) => e.key === "Enter" && setSelectedGuid(child.guid)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1,
                                py: 0.6,
                                borderRadius: 2,
                                cursor: "pointer",
                                "&:hover": { bgcolor: brand.canvas },
                              }}
                            >
                              <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {child.name}
                              </Typography>
                              <ChevronRightRoundedIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Surface>

            {/* Diagrams */}
            <Surface sx={{ p: 2.5 }}>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Diagrams and files</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                The viewer shows the current diagram of each format; other files are kept to download. Older files stay as history and can be restored.
              </Typography>

              <Stack spacing={1}>
                {versions.length === 0 && (
                  <Stack alignItems="center" spacing={0.5} sx={{ py: 2 }}>
                    <ImageRoundedIcon sx={{ color: "text.disabled" }} />
                    <Typography variant="body2" sx={{ fontWeight: 650 }}>
                      No diagram or file yet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {mayManage ? "Attach one below; it shows in the viewer straight away." : "Ask your workspace admin for Edit rights to attach one."}
                    </Typography>
                  </Stack>
                )}
                {versions.map((version) => (
                  <Stack
                    key={version.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      p: 1.25,
                      borderRadius: 3,
                      border: `1px solid ${version.active ? brand.ink : brand.line}`,
                      bgcolor: version.active ? brand.markSoft : "transparent",
                    }}
                  >
                    <Box sx={{ color: brand.inkSoft, display: "grid", placeItems: "center" }}>
                      {version.format === "bpmn" ? <AccountTreeRoundedIcon fontSize="small" /> : <ImageRoundedIcon fontSize="small" />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {(version.format || "file").toUpperCase()}
                          {version.version ? ` v${version.version}` : ""}
                        </Typography>
                        {version.active ? (
                          <Chip size="small" label="Current" sx={{ height: 18, bgcolor: brand.ink, color: "#fff", fontWeight: 700 }} />
                        ) : (
                          <Chip size="small" label="History" sx={{ height: 18 }} />
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                          {version.fileName || version.name || "unnamed"} · {formatSize(version.bytes)}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {version.active
                          ? `Attached ${formatDate(version.createdAt)}${version.createdBy ? ` by ${version.createdBy}` : ""}`
                          : `Replaced ${formatDate(version.archivedAt)}${version.archivedBy ? ` by ${version.archivedBy}` : ""}`}
                      </Typography>
                    </Box>
                    {version.active && version.format && (
                      <Tooltip title="Download this file">
                        <IconButton
                          size="small"
                          disabled={busy === `download-${version.id}`}
                          onClick={() => download(version)}
                          aria-label={`Download ${(version.format || "file").toUpperCase()}`}
                        >
                          <DownloadRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {mayManage &&
                      (version.active ? (
                        <Tooltip title="Archive: the viewer stops showing it, the file stays">
                          <IconButton
                            size="small"
                            disabled={busy === `archive-${version.id}`}
                            onClick={() => run(`archive-${version.id}`, () => archiveDiagramVersion(database, object.GUID, version.id), "Diagram archived.")}
                            aria-label="Archive diagram"
                            sx={quietDanger}
                          >
                            <ArchiveRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Make this the current diagram again">
                          <IconButton
                            size="small"
                            disabled={busy === `restore-${version.id}`}
                            onClick={() => run(`restore-${version.id}`, () => restoreDiagramVersion(database, object.GUID, version.id), "Diagram restored.")}
                            aria-label="Restore diagram"
                          >
                            <RestoreRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ))}
                  </Stack>
                ))}
              </Stack>

              {mayManage && (
                <FileUpload
                  database={database}
                  guid={object.GUID}
                  onError={setError}
                  onAttached={async (message) => {
                    setToast(message);
                    await loadSelection(selectedGuid);
                  }}
                />
              )}
            </Surface>

            {/* Texts and attributes */}
            <Surface>
              <Box sx={{ p: 2.5, pb: 1.5 }}>
                <Typography sx={{ fontWeight: 700 }}>Texts and attributes</Typography>
                <Typography variant="body2" color="text.secondary">
                  What readers see in the properties panel. Which of these appear, and in what order, is set under Properties panel.
                </Typography>
              </Box>
              <Stack spacing={1.5} sx={{ px: 2.5, pb: 2 }}>
                {editableFields.map((field) => (
                  <TextField
                    key={field.key}
                    size="small"
                    label={field.label}
                    value={fields[field.key] || ""}
                    disabled={!mayManage}
                    multiline={field.rows > 1}
                    rows={field.rows > 1 ? field.rows : undefined}
                    onChange={(e) => setFields((f) => ({ ...f, [field.key]: e.target.value }))}
                  />
                ))}
                {mayManage && (
                  <Button startIcon={<AddRoundedIcon />} onClick={() => setAttributeOpen(true)} sx={{ alignSelf: "flex-start" }}>
                    Add an attribute
                  </Button>
                )}
              </Stack>
              {mayManage && (
                // Stays in view while the texts scroll, so a change is never
                // left unsaved by accident.
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    px: 2.5,
                    py: 1.5,
                    borderTop: `1px solid ${brand.line}`,
                    bgcolor: dirty ? brand.markSoft : "#fff",
                  }}
                >
                  <Typography variant="caption" sx={{ flex: 1, fontWeight: 600, color: dirty ? brand.ink : "text.secondary" }}>
                    {dirty ? "Unsaved changes" : "All changes saved"}
                  </Typography>
                  {dirty && <Button size="small" onClick={() => setFields(fieldsFromObject(object, editableFields))}>Discard</Button>}
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!dirty || busy === "save"}
                    startIcon={busy === "save" ? <CircularProgress size={16} color="inherit" /> : null}
                    onClick={() => run("save", () => updateContentObject(database, object.GUID, fields), "Saved.")}
                  >
                    Save
                  </Button>
                </Stack>
              )}
            </Surface>

            {/* Restorable links */}
            {unlinked.length > 0 && (
              <Surface sx={{ p: 2.5 }}>
                <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Removed links</Typography>
                <Stack spacing={1}>
                  {unlinked.map((entry) => (
                    <Stack key={entry.id} direction="row" spacing={1} alignItems="center">
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                          {entry.childName} → {entry.parentName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entry.relationship} · removed {formatDate(entry.archivedAt)}
                          {entry.archivedBy ? ` by ${entry.archivedBy}` : ""}
                        </Typography>
                      </Box>
                      {mayManage && (
                        <Button
                          size="small"
                          startIcon={<RestoreRoundedIcon />}
                          disabled={busy === `link-${entry.id}`}
                          onClick={() => run(`link-${entry.id}`, () => restoreContentLink(database, entry.id), "Link restored.")}
                        >
                          Restore
                        </Button>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </Surface>
            )}
          </Box>
        )}
      </Box>

      <CreateObjectDialog
        open={createOpen}
        database={database}
        parent={createParent}
        protectedFields={viewConfig?.protectedFields}
        onClose={() => setCreateOpen(false)}
        onCreate={async (payload) => {
          const res = await createContentObject(database, payload);
          if (res?.success) {
            setCreateOpen(false);
            setToast(res.msg || "Object created.");
            setSelectedGuid(res.content.guid);
            setQuery("");
            return true;
          }
          setError(res?.msg || "Couldn't create the object.");
          return false;
        }}
      />

      <AddAttributeDialog
        open={attributeOpen}
        database={database}
        type={object?.Type}
        existing={editableFields.map(({ key }) => key)}
        onClose={() => setAttributeOpen(false)}
        onAdd={(name) => {
          setAddedFields((current) => [...current, name]);
          setFields((current) => ({ ...current, [name]: "" }));
          setAttributeOpen(false);
        }}
      />

      <Toast notice={toast} onClose={() => setToast("")} />
    </Box>
  );
}

// Adds an attribute to this object. Names already used by objects of the same
// type are offered first, so a database keeps one spelling per attribute.
function AddAttributeDialog({ open, database, type, existing, onClose, onAdd }) {
  const [known, setKnown] = useState([]);
  const [name, setName] = useState("");

  useEffect(() => {
    if (!open || !type || !database) return;
    setName("");
    listUsedAttributes(database, type).then((res) => {
      if (!res?.success) return;
      setKnown([...new Set(res.content || [])].sort((a, b) => a.localeCompare(b)));
    });
  }, [open, type, database]);

  const trimmed = name.trim();
  const alreadyThere = existing.includes(trimmed);
  const validName = /^[A-Za-z][A-Za-z0-9 _-]{0,59}$/.test(trimmed);
  const options = known.filter((attribute) => !existing.includes(attribute));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ className: "bfx", sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Add an attribute</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The attribute is added to this {type || "object"} when you save. To show it to readers, add it under Properties panel.
        </Typography>
        <Autocomplete
          freeSolo
          options={options}
          value={name}
          onChange={(event, next) => setName(next || "")}
          onInputChange={(event, next) => setName(next || "")}
          componentsProps={{
            paper: { className: "bfx", sx: { borderRadius: 3, border: `1px solid ${brand.line}`, mt: 0.5 } },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              size="small"
              label="Attribute name"
              helperText={
                alreadyThere
                  ? "This object already has that attribute."
                  : trimmed && !validName
                  ? "Letters, digits, spaces, dashes and underscores; start with a letter."
                  : options.length
                  ? "Pick one already used by this type, or type a new name."
                  : "Letters, digits, spaces, dashes and underscores."
              }
              error={Boolean(trimmed) && (!validName || alreadyThere)}
            />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!validName || alreadyThere} onClick={() => onAdd(trimmed)}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
